import { API_BASE_URL } from './config';
import React, { useEffect, useRef, useState } from 'react';
import { Cloud } from 'lucide-react';
import secureStorage from './utils/secureStorage';

const API = API_BASE_URL;

// Read exercise id from the URL hash query, e.g. #display-weather?exercise=abc
const getExerciseIdFromHash = () => {
  try {
    const raw = (window.location.hash || '').substring(1);
    const q = raw.split('?')[1];
    if (!q) return null;
    const params = new URLSearchParams(q);
    return params.get('exercise') || params.get('exercise_id') || null;
  } catch (_) {
    return null;
  }
};

const DisplayWeather = () => {
  const [satelliteImageUrl, setSatelliteImageUrl] = useState(null);
  const [locationInfo, setLocationInfo] = useState({ city: '', province: '' });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [customImage, setCustomImage] = useState(null); // { blobUrl, uploaded_at, uploaded_by, original_name }
  const blobUrlRef = useRef(null);
  const exerciseId = getExerciseIdFromHash();

  const clearBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  const loadCustomWeather = async (token) => {
    if (!exerciseId || !token) return false;
    try {
      // Fetch the exercise metadata to know if a custom image exists
      const exResp = await fetch(`${API}/api/exercise-builder/${exerciseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!exResp.ok) return false;
      const ex = await exResp.json();
      if (!ex?.custom_weather_image_path) {
        clearBlobUrl();
        setCustomImage(null);
        return false;
      }
      const imgResp = await fetch(`${API}/api/exercise-builder/${exerciseId}/weather-image`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!imgResp.ok) return false;
      const blob = await imgResp.blob();
      clearBlobUrl();
      const blobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = blobUrl;
      setCustomImage({
        blobUrl,
        uploaded_at: ex.custom_weather_image_uploaded_at,
        uploaded_by: ex.custom_weather_image_uploaded_by,
        original_name: ex.custom_weather_image_original_name,
      });
      setLocationInfo({
        city: ex.exercise_name || '',
        province: ex.location || '',
      });
      return true;
    } catch (err) {
      console.error('Custom weather load failed:', err);
      return false;
    }
  };

  const loadDefaultWeather = async (token) => {
    try {
      const prefResponse = await fetch(`${API}/api/users/weather-preference`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (prefResponse.ok) {
        const preference = await prefResponse.json();
        setLocationInfo({ city: preference.city, province: preference.province });
        const rssResponse = await fetch(
          `${API}/api/weather-locations/rss/${encodeURIComponent(preference.province)}/${encodeURIComponent(preference.city)}`
        );
        if (rssResponse.ok) {
          const rssResult = await rssResponse.json();
          setSatelliteImageUrl(rssResult.rss_feed);
        }
      }
    } catch (err) {
      console.error('Default weather load failed:', err);
    }
  };

  const loadWeather = async () => {
    try {
      const token = secureStorage.getToken();
      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }

      // Prefer the exercise's custom GFA image when available
      const usedCustom = await loadCustomWeather(token);
      if (!usedCustom) {
        setCustomImage(null);
        clearBlobUrl();
        await loadDefaultWeather(token);
      }
      setLoading(false);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading weather:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWeather();
    // Refresh every 60s so newly-uploaded GFA images show up on the big board quickly
    const refreshInterval = setInterval(() => {
      loadWeather();
    }, 60000);
    return () => {
      clearInterval(refreshInterval);
      clearBlobUrl();
    };
  }, []);

  if (loading) {
    return (
      <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading Weather Data...</div>
      </div>
    );
  }

  if (!customImage && !satelliteImageUrl) {
    return (
      <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Cloud className="h-24 w-24 text-gray-600 mx-auto mb-4" />
          <p className="text-white text-2xl">No Weather Data Available</p>
          <p className="text-gray-400 mt-2">Please select a weather location first</p>
        </div>
      </div>
    );
  }

  const isCustom = Boolean(customImage);

  return (
    <div className="w-screen h-screen bg-gray-900 overflow-hidden flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 border-b border-orange-500 px-6 py-4 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 flex items-center gap-3">
            EXRSIM - Weather Display
            {isCustom && (
              <span className="px-2 py-0.5 text-xs rounded bg-orange-600 text-white uppercase tracking-wide" data-testid="display-custom-badge">
                Custom GFA
              </span>
            )}
          </h1>
          <p className="text-lg text-gray-400">
            {isCustom
              ? (customImage.original_name || 'Simulated Weather Event')
              : (locationInfo.city && locationInfo.province
                  ? `Satellite Image: ${locationInfo.city}, ${locationInfo.province}`
                  : 'Loading location...')}
          </p>
          {isCustom && customImage.uploaded_by && (
            <p className="text-sm text-gray-500">
              Uploaded by {customImage.uploaded_by}
              {customImage.uploaded_at ? ` · ${new Date(customImage.uploaded_at).toLocaleString()}` : ''}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-400">Last Updated</p>
          <p className="text-xl text-white">{lastUpdate.toLocaleTimeString()}</p>
        </div>
      </div>

      {/* Image Display */}
      <div className="flex-1 flex items-center justify-center bg-black p-4">
        <img
          src={isCustom ? customImage.blobUrl : satelliteImageUrl}
          alt={isCustom
            ? (customImage.original_name || 'Custom GFA weather image')
            : `Weather satellite for ${locationInfo.city}, ${locationInfo.province}`}
          className="max-w-full max-h-full object-contain"
          style={{ imageRendering: 'auto' }}
          data-testid={isCustom ? 'display-custom-weather' : 'display-default-weather'}
        />
      </div>
    </div>
  );
};

export default DisplayWeather;
